import { useContext, useEffect, useState } from 'react';

import { getUnifiedCodeLanguageId } from '../../utils/getUnifiedCodeLanguageId';
import { PageContext } from '../context/PageContext';
import { SessionCodeLanguageContext } from '../context/SessionCodeLanguageContext';

interface Props {
  languages: string[];
}

export const useCodeLanguage = ({
  languages,
}: Props): [number, (value: number) => void] => {
  const { defaultCodeLanguage } = useContext(PageContext);
  const { sessionLanguages, setSessionLanguage } = useContext(
    SessionCodeLanguageContext,
  );

  const [language, setLanguage] = useState(0);

  useEffect(() => {
    let index = 0;
    // clean up and unify languages since they are entered by the user
    const unifiedLanguages = languages.map(lang =>
      getUnifiedCodeLanguageId(lang.toLocaleLowerCase()),
    );
    const unifiedSessionLanguages = sessionLanguages.map(lang =>
      getUnifiedCodeLanguageId(lang.toLocaleLowerCase()),
    );

    // clean up and unify default code language
    const unifiedDefaultLanguage = defaultCodeLanguage
      ? getUnifiedCodeLanguageId(defaultCodeLanguage.toLocaleLowerCase())
      : undefined;

    if (unifiedSessionLanguages.length) {
      // intersect both arrays and find the the index of the first language in the labels array
      const firstLanguage = unifiedSessionLanguages.find(x =>
        unifiedLanguages.some(y => x.toLocaleLowerCase() === y),
      );

      if (firstLanguage) {
        index = unifiedLanguages.findIndex(x => x === firstLanguage);
      }
    } else if (!unifiedSessionLanguages.length && unifiedDefaultLanguage) {
      index = unifiedLanguages.indexOf(unifiedDefaultLanguage);
    }

    setLanguage(index > -1 ? index : 0);
  }, [languages, defaultCodeLanguage, sessionLanguages]);

  const handleChangeLanguage = (value: number) => {
    setLanguage(value);
    setSessionLanguage(languages[value]);
  };

  return [language, handleChangeLanguage];
};
